import { diffLines } from 'diff';
import { LIVE_MOVED_TEXT, TOO_LONG_TEXT } from './agentClient';
import type {
  AgentSkill,
  AgentsCatalog,
  SkillDoc,
  SkillDraft,
  SkillHistory,
  SkillRefusal,
  SkillVersion,
} from './types';

export type DiffLine = { kind: 'same' | 'added' | 'removed'; text: string };
export type ButtonState = 'hidden' | 'enabled' | 'disabled';

/** A line diff, one entry per line. */
export const lineDiff = (before: string, after: string): DiffLine[] =>
  diffLines(before, after).flatMap((part) => {
    const kind = part.added ? 'added' : part.removed ? 'removed' : 'same';
    const lines = part.value.split('\n');
    if (lines[lines.length - 1] === '') lines.pop();
    return lines.map((text) => ({ kind, text }) as DiffLine);
  });

/** Local short date and time, as the rest of the panel shows dates; nothing for a missing or invalid date. */
export const shortDate = (iso: string): string => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toLocaleString() : '';
};

export const SKILLS_STALE_TEXT =
  "Skills couldn't be refreshed from Foundry; showing the last versions fetched.";

/** The Agents view's note when the catalog's skills are the last ones fetched (spec §4.4). */
export const staleNote = (catalog: AgentsCatalog | null): string | null =>
  catalog?.skillsStale ? SKILLS_STALE_TEXT : null;

/** Edit needs the live version from the catalog; an older service sends none and can't edit. */
export const canEdit = (skill: AgentSkill): boolean => skill.version !== '';

/** Fetch a skill not fetched yet, or retry one whose fetch failed. */
export const needsLoad = (
  body: { status: 'loading' | 'ready' | 'error' } | undefined,
): boolean => !body || body.status === 'error';

/** Which text the editor holds. */
export const editorSource = (doc: SkillDoc): string => {
  if (doc.myDraft)
    return `Your draft v${doc.myDraft.version} · your own chats and crew runs use it`;
  return doc.version ? `Live version v${doc.version}` : 'Live version';
};

/** The person's draft was based on an older live version: publishing it would be refused. */
export const draftBehind = (doc: SkillDoc): boolean =>
  !!doc.myDraft && doc.version !== '' && doc.myDraft.basedOn !== doc.version;

/** The live_moved refusal is shown as the "behind" notice, so not again as an error. */
export const errorToShow = (
  error: string | null,
  behind: boolean,
): string | null => (behind && error === LIVE_MOVED_TEXT ? null : error);

/** The text after a successful save: the service's canonical text, unless the person kept typing. */
export const afterSave = (
  current: string,
  sent: string,
  saved: string,
): string => (current === sent ? saved : current);

/** Copying another person's draft replaces the caller's own, which can't be recovered. */
export const copyWarning = (doc: SkillDoc): string | null =>
  doc.myDraft
    ? "This replaces your own draft of this skill, and your draft's text can't be recovered."
    : null;

/** The service's request limit, in characters (its 32,000 limit on the saved text still applies). */
const MAX_SEND_CHARS = 64_000;

/** A save the editor refuses itself, before sending: over the request limit. */
export const localSaveRefusal = (text: string): string | null =>
  // UTF-16 units are never fewer than characters, so short text needs no count.
  text.length > MAX_SEND_CHARS && Array.from(text).length > MAX_SEND_CHARS
    ? TOO_LONG_TEXT
    : null;

export const versionRow = (
  v: SkillVersion,
): { title: string; detail: string } => {
  const parts = [v.authorName ?? v.author, shortDate(v.at)].filter(Boolean);
  if (v.reason?.trim()) parts.push(v.reason.trim());
  if (v.copiedFrom) parts.push(`based on a draft by ${v.copiedFrom}`);
  return {
    title: `v${v.version}${v.live ? ' · live' : ''}`,
    detail: parts.join(' · '),
  };
};

/** A draft behind live can be saved unchanged (that rebases it), and can't be published until it is. */
export const editorButtons = (
  doc: SkillDoc,
  dirty: boolean,
  busy: boolean,
): { save: ButtonState; discard: ButtonState; publish: ButtonState } => {
  const behind = draftBehind(doc);
  return {
    save: (dirty || behind) && !busy ? 'enabled' : 'disabled',
    discard: doc.myDraft ? (busy ? 'disabled' : 'enabled') : 'hidden',
    publish: !doc.canPublish
      ? 'hidden'
      : doc.myDraft && !dirty && !busy && !behind
        ? 'enabled'
        : 'disabled',
  };
};

/** A reason or note: 1 to 200 characters after trimming, one line. */
export const oneLineOk = (text: string): boolean => {
  const t = text.trim();
  return t.length >= 1 && t.length <= 200 && !/[\r\n]/.test(t);
};

export const KIND_LABELS: Record<string, string> = {
  tfn_like: 'a TFN-like number',
  account_like: 'an account-like number',
  bsb: 'a BSB',
  passport_like: 'a passport-like number',
  email: 'an email address',
  phone: 'a phone number',
  card_like: 'a card-like number',
  medicare_like: 'a Medicare-like number',
  uuid: 'a record ID',
  image_link: 'an image link',
  amount: 'an amount',
};

export const refusalText = (r: SkillRefusal): string => {
  if (r.code !== 'client_data') return '';
  const parts = r.found.map(
    (f) =>
      `${Object.prototype.hasOwnProperty.call(KIND_LABELS, f.kind) ? KIND_LABELS[f.kind] : 'client data'} (line ${f.line})`,
  );
  return `Found: ${parts.join(', ')}`;
};

/** Open drafts by other people: the caller's own draft is the one in the editor. */
export const otherDrafts = (
  history: SkillHistory,
  doc: SkillDoc,
): SkillDraft[] =>
  history.drafts.filter((d) => d.version !== doc.myDraft?.version);

const TRIAGE_HINT =
  "Try a draft: post a report in the test channel. Your reports there use your draft; everyone else's, and every other channel, use the published version.";
const INTRO_HINT =
  'Try a draft: send it to the test channel from Send to Slack with "Send my draft" ticked. {mimir} becomes Mimir\'s @-mention.';

/** A line above the editor for Mimir's skills, saying how to try a draft; null for any other skill. */
export const skillHint = (name: string): string | null =>
  name === 'mimir-triage'
    ? TRIAGE_HINT
    : name.startsWith('mimir-intro-')
      ? INTRO_HINT
      : null;
